import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ImageIcon } from 'lucide-react';
import { useMyMenus, useCreateMenu, useAddItemsToMenu, MENU_ITEM_CAP } from '@/hooks/useMenus';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { GlassModal } from '@/components/ui/glass-modal';

interface Props {
	open: boolean;
	onOpenChange: (b: boolean) => void;
	itemIds: string[];
	onDone?: () => void;
}

type Mode = 'new' | 'existing';

export function SaveToMenuDialog({ open, onOpenChange, itemIds, onDone }: Props) {
	const navigate = useNavigate();
	const { data: menus } = useMyMenus();
	const create = useCreateMenu();
	const add = useAddItemsToMenu();
	const [mode, setMode] = useState<Mode>('new');
	const [name, setName] = useState('');
	const [pickedMenuId, setPickedMenuId] = useState<string | null>(null);

	const count = itemIds.length;
	const tooMany = count > MENU_ITEM_CAP;
	const limitedIds = itemIds.slice(0, MENU_ITEM_CAP);

	const reset = () => {
		setMode('new');
		setName('');
		setPickedMenuId(null);
	};

	const submit = async () => {
		try {
			if (mode === 'new') {
				if (!name.trim()) {
					toast.error('Give the menu a name');
					return;
				}
				const menu = await create.mutateAsync({
					name: name.trim(),
					item_ids: limitedIds
				});
				toast.success(tooMany ? `Saved ${limitedIds.length} of ${count} (cap is ${MENU_ITEM_CAP})` : 'Menu created');
				reset();
				onOpenChange(false);
				onDone?.();
				navigate(`/menus/${menu.id}`);
			} else {
				if (!pickedMenuId) {
					toast.error('Pick a menu');
					return;
				}
				const res = await add.mutateAsync({ menu_id: pickedMenuId, user_item_ids: limitedIds });
				toast.success(
					res.skipped > 0
						? `Added ${res.added} · skipped ${res.skipped} (already in menu or cap reached)`
						: `Added ${res.added}`
				);
				reset();
				onOpenChange(false);
				onDone?.();
			}
		} catch (e) {
			toast.error((e as Error).message || 'Could not save to menu');
		}
	};

	return (
		<GlassModal
			open={open}
			onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}
			title={`Save ${count} ${count === 1 ? 'item' : 'items'} to a menu`}
		>
			<h2 className="text-lg font-semibold">
				Save {count} {count === 1 ? 'item' : 'items'} to a menu
			</h2>

			{tooMany && (
				<p className="text-[12px] text-warning/90 mt-2">
					Menus are capped at {MENU_ITEM_CAP} items. Only the first {MENU_ITEM_CAP} will be added.
				</p>
			)}

			<div className="grid grid-cols-2 gap-2 my-3">
				<button
					type="button"
					onClick={() => setMode('new')}
					className={cn(
						'rounded-lg border p-3 text-left transition-colors',
						mode === 'new' ? 'bg-active-gold/10 border-active-gold/40 text-active-gold' : 'bg-white/[0.04] border-white/[0.06]'
					)}
				>
					<p className="text-sm font-semibold">New menu</p>
					<p className="text-[11px] text-muted-foreground mt-0.5">Name it now</p>
				</button>
				<button
					type="button"
					onClick={() => setMode('existing')}
					disabled={!menus || menus.length === 0}
					className={cn(
						'rounded-lg border p-3 text-left transition-colors',
						mode === 'existing' ? 'bg-active-gold/10 border-active-gold/40 text-active-gold' : 'bg-white/[0.04] border-white/[0.06]',
						(!menus || menus.length === 0) && 'opacity-40 cursor-not-allowed'
					)}
				>
					<p className="text-sm font-semibold">Add to existing</p>
					<p className="text-[11px] text-muted-foreground mt-0.5">
						{menus?.length || 0} saved menu{(menus?.length || 0) === 1 ? '' : 's'}
					</p>
				</button>
			</div>

			{mode === 'new' ? (
				<div className="space-y-2">
					<Label htmlFor="save-menu-name">Menu name</Label>
					<Input
						id="save-menu-name"
						placeholder="e.g. Heavy Hitters"
						value={name}
						onChange={(e) => setName(e.target.value)}
						maxLength={60}

						onKeyDown={(e) => {
							if (e.key === 'Enter') submit();
						}}
					/>
				</div>
			) : (
				<ScrollArea className="max-h-56">
					<div className="space-y-1">
						{(menus || []).map((m) => (
							<button
								key={m.id}
								type="button"
								onClick={() => setPickedMenuId(m.id)}
								className={cn(
									'w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors',
									pickedMenuId === m.id ? 'bg-active-gold/10 border border-active-gold/40' : 'bg-white/[0.03] border border-transparent hover:bg-white/[0.05]'
								)}
							>
								<div className="w-9 h-9 rounded-md bg-black/40 overflow-hidden flex items-center justify-center shrink-0">
									{m.last_export_url ? <img src={m.last_export_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{m.name}</p>
									<p className="text-[11px] text-muted-foreground">{m.template_key.replace('_', ' ')}</p>
								</div>
							</button>
						))}
					</div>
				</ScrollArea>
			)}

			<div className="flex justify-end gap-2 pt-4">
				<Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
				<Button
					onClick={submit}
					disabled={create.isPending || add.isPending || (mode === 'new' ? !name.trim() : !pickedMenuId)}
				>
					{create.isPending || add.isPending ? 'Saving…' : 'Save to menu'}
				</Button>
			</div>
		</GlassModal>
	);
}
